import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { NotificationBell } from "../../../components/ui/NotificationBell";
import { NotificationPanel, NotificationFilter } from "../../../components/ui/NotificationPanel";
import { NotificationItem } from "../../../components/ui/NotificationItem";
import {
    useStoreNotifications,
    useStoreUnreadNotificationCount,
} from "../../../hooks/store/useStoreNotifications";
import { StoreNotification } from "../../../types/notification";
import { getUserErrorMessage } from "../../../api/apiErrors";
import { showError } from "../../../utils/alert";
import { formatRelativeTime } from "../../../utils/formatRelativeTime";
import {
    markStoreNotificationAsRead,
    markAllStoreNotificationsAsRead,
} from "../../../utils/storeNotificationService";

const STORE_NOTIFICATION_FILTERS: NotificationFilter[] = [
    { key: "", label: "All" },
    { key: "store", label: "Store" },
    { key: "financial", label: "Financial" },
    { key: "cart", label: "Cart" },
];

const PAGE_LIMIT = 6;

export const StoreNotificationBell: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedFilter, setSelectedFilter] = useState("");
    const [allNotifications, setAllNotifications] = useState<StoreNotification[]>([]);
    const [markingIds, setMarkingIds] = useState<number[]>([]);

    const { data: notificationsData, isLoading } = useStoreNotifications({
        page: currentPage,
        limit: PAGE_LIMIT,
        type: selectedFilter,
    });

    const { data: unreadCount = 0 } = useStoreUnreadNotificationCount();

    useEffect(() => {
        if (!notificationsData?.data) return;
        setAllNotifications(notificationsData.data);
    }, [notificationsData?.data]);

    const handleMarkAsRead = useCallback(
        async (notificationId: number) => {
            const notification = allNotifications.find((n) => n.id === notificationId);
            if (!notification || notification.isRead) return;

            setMarkingIds((prev) => [...prev, notificationId]);
            setAllNotifications((prev) =>
                prev.map((n) =>
                    n.id === notificationId ? { ...n, isRead: true } : n
                )
            );

            try {
                await markStoreNotificationAsRead(notificationId);
                queryClient.invalidateQueries({ queryKey: ["store-notifications-unread-count"] });
            } catch (error) {
                setAllNotifications((prev) =>
                    prev.map((n) =>
                        n.id === notificationId ? { ...n, isRead: false } : n
                    )
                );
                showError(getUserErrorMessage(error));
            } finally {
                setMarkingIds((prev) => prev.filter((id) => id !== notificationId));
            }
        },
        [allNotifications, queryClient]
    );

    const handleMarkAllAsRead = useCallback(async () => {
        if (unreadCount === 0) return;

        setAllNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));

        try {
            await markAllStoreNotificationsAsRead();
            queryClient.invalidateQueries({ queryKey: ["store-notifications-unread-count"] });
            queryClient.invalidateQueries({ queryKey: ["store-notifications"] });
        } catch (error) {
            queryClient.invalidateQueries({ queryKey: ["store-notifications"] });
            showError(getUserErrorMessage(error));
        } finally {
        }
    }, [unreadCount, queryClient]);



    const handleFilterChange = useCallback((filterKey: string) => {
        setSelectedFilter(filterKey);
        setCurrentPage(1);
    }, []);

    const handleNotificationClick = useCallback(
        (notification: StoreNotification, closePanel: () => void) => {
            if (!notification.isRead) {
                handleMarkAsRead(notification.id);
            }

            if (notification.key && notification.key.startsWith("store")) {
                navigate("/store/dashboard");
            } else if (notification.type === "financial") {
                navigate("/store/transactions");
            } else if (notification.type === "cart") {
                navigate("/store/cart");
            } else {
                navigate("/store/dashboard");
            }

            closePanel();
        },
        [handleMarkAsRead, navigate]
    );

    const handlePreviousPage = useCallback(() => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    }, []);

    const handleNextPage = useCallback(() => {
        if (!notificationsData?.totalPages) return;
        setCurrentPage((prev) => Math.min(prev + 1, notificationsData.totalPages));
    }, [notificationsData?.totalPages]);

    return (
        <NotificationBell
            unreadCount={unreadCount}
            ariaLabel={t("notifications.title", "Notifications")}
            renderPanel={(isOpen, onClose) => (
                <NotificationPanel
                    isOpen={isOpen}
                    onClose={onClose}
                    title={t("notifications.title", "Notifications")}
                    unreadCount={unreadCount}
                    notifications={allNotifications}
                    isLoading={isLoading}
                    isLoadingMore={false}
                    filters={STORE_NOTIFICATION_FILTERS}
                    selectedFilter={selectedFilter}
                    onFilterChange={handleFilterChange}
                    onMarkAllAsRead={handleMarkAllAsRead}
                    onLoadMore={undefined}
                    onPreviousPage={handlePreviousPage}
                    onNextPage={handleNextPage}
                    canPreviousPage={currentPage > 1}
                    canNextPage={Boolean(notificationsData && currentPage < notificationsData.totalPages)}
                    previousLabel={t("notifications.previous", "Previous")}
                    nextLabel={t("notifications.next", "Next")}
                    loadingLabel={t("notifications.loading", "Loading...")}
                    pageInfo={{
                        current: currentPage,
                        total: notificationsData?.totalPages || 1,
                    }}
                    pageInfoText={t("notifications.pageInfo", "Page {{current}} of {{total}}", {
                        current: currentPage,
                        total: notificationsData?.totalPages || 1,
                    })}
                    renderNotification={(notification) => (
                        <NotificationItem
                            key={notification.id}
                            title={notification.content}
                            content={notification.source}
                            date={formatRelativeTime(notification.createdAt)}
                            icon={notification.type === "financial" ? "credit-card" : notification.type === "cart" ? "shopping-cart" : "home"}
                            colorClass={notification.type === "financial" ? "bg-blue-50" : notification.type === "cart" ? "bg-yellow-50" : "bg-slate-100"}
                            isRead={notification.isRead}
                            isLoading={markingIds.includes(notification.id)}
                            onMarkAsRead={() => handleMarkAsRead(notification.id)}
                            onClick={() => handleNotificationClick(notification, onClose)}
                        />
                    )}
                />
            )}
        />
    );
};
